import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import {
  fetchUser,
  selectUserObject,
  selectUserCallStatus,
  selectUserCallError,
  updateUser,
} from '../../features/user/userSlice'
import {
  selectProfilePictureStatus,
  fetchProfilePicture,
  selectProfilePictureImage,
  deleteProfilePicture,
} from '../../features/user/profilePictureSlice'
import styles from './MyAccount.module.scss'
import { LoadingIcon } from '../../features/section-components/LoadingIcon'
import MarginWrapper from '../../features/wrappers/MarginWrapper'
import isMobileView from '../../util/MobileView'
import MyAccountMobile from './MyAccountMobile'
import MyAccountDesktop from './MyAccountDesktop'
import { MyAccountManager } from './MyAccountManager'
import { LanguageSelectionManager } from '../../features/language-selector/LanguageSelectionManager'
import { EditAccountManager } from '../../features/EditAccount/EditAccountManager'
import { renderData } from '../../util/renderData'
import { formatPhoneToNational } from '../../util/phoneUtils'
import { selectUserPhone } from '../EditPhoneNumber/userPhoneSlice'

export function MyAccount() {
  const location = useLocation()
  const editNameStatus = location?.state?.editNameStatus
  const editLanguageStatus = location?.state?.editLanguageStatus
  const editSecondaryEmailStatus = location?.state?.editSecondaryEmailStatus
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const isMobile = isMobileView()
  const { phoneNumber, status } = useSelector(selectUserPhone)
  const userCallStatus = useSelector(selectUserCallStatus)
  const userObj = useSelector(selectUserObject)
  const userCallError = useSelector(selectUserCallError)
  const userProfilePictureStatus = useSelector(selectProfilePictureStatus)
  const userProfilePictureData = useSelector(selectProfilePictureImage)

  const navigate = useNavigate()
  const [pictureStatus, setPictureStatus] = useState(null)

  // A new edit banner (delivered via navigation state) replaces the picture banner
  useEffect(() => {
    if (editNameStatus || editLanguageStatus || editSecondaryEmailStatus) {
      setPictureStatus(null)
    }
  }, [location.key])

  // A picture banner replaces any edit banner currently coming from navigation state
  const showPictureStatus = (pictureAction) => {
    if (editNameStatus || editLanguageStatus || editSecondaryEmailStatus) {
      navigate(location.pathname, { replace: true, state: {} })
    }
    setPictureStatus(pictureAction)
  }

  const handlePhotoDelete = async () => {
    try {
      await dispatch(deleteProfilePicture()).unwrap()
      showPictureStatus('delete')
    } catch (error) {
      setPictureStatus(null)
    }
  }

  const {
    isPageLoading,
    fullName,
    preferredLanguage,
    languageSelectorComponent,
    setAlertVisible,
    languageOptions,
    editAccountComponent,
    showEditAccount,
    showProfileInfo,
    toggleEditAccount,

  } = MyAccountManager({
    dispatch,
    t,
    userCallStatus,
    userObj: userObj || {},
    userCallError,
    userProfilePictureStatus,
    userProfilePictureData,
    fetchUser,
    fetchProfilePicture,
    LanguageSelectionManager,
    EditAccountManager,
    updateUser,
    handlePhotoDelete,
    phoneNumber,
    phoneNumberStatus: status
  })

  if (userCallError && userCallError.message) {
    return (
      <div className={styles['error-message']}>
        <p>Error: {userCallError.message}</p>
      </div>
    )
  }

  const renderDataWithMobileCheck = (label, value) => renderData(isMobile, label, value)

  return (
    <MarginWrapper>
      <LoadingIcon isLoading={isPageLoading}>
        {isMobile ? (
          <MyAccountMobile
            t={t}
            userObj={userObj || {}}
            userProfilePictureData={userProfilePictureData}
            fullName={fullName}
            isPageLoading={isPageLoading}
            isMobile={isMobile}
            renderData={renderDataWithMobileCheck}
            preferredLanguage={preferredLanguage}
            languageSelectorComponent={languageSelectorComponent}
            languageOptions={languageOptions}
            setAlertVisible={setAlertVisible}
            editAccountComponent={editAccountComponent}
            toggleEditAccount={toggleEditAccount}
            showEditAccount={showEditAccount}
            showProfileInfo={showProfileInfo}
            formatPhoneToNational={formatPhoneToNational}
            handlePhotoDelete={handlePhotoDelete}
            phoneNumber={phoneNumber}
            editNameStatus={editNameStatus}
            editLanguageStatus={editLanguageStatus}
            editSecondaryEmailStatus={editSecondaryEmailStatus}
            pictureStatus={pictureStatus}
            setPictureStatus={setPictureStatus}
            showPictureStatus={showPictureStatus}
          />
        ) : (
          <MyAccountDesktop
            t={t}
            userObj={userObj || {}}
            userProfilePictureData={userProfilePictureData}
            fullName={fullName}
            isPageLoading={isPageLoading}
            isMobile={isMobile}
            renderData={renderDataWithMobileCheck}
            preferredLanguage={preferredLanguage}
            languageSelectorComponent={languageSelectorComponent}
            languageOptions={languageOptions}
            setAlertVisible={setAlertVisible}
            editAccountComponent={editAccountComponent}
            toggleEditAccount={toggleEditAccount}
            showEditAccount={showEditAccount}
            showProfileInfo={showProfileInfo}
            formatPhoneToNational={formatPhoneToNational}
            handlePhotoDelete={handlePhotoDelete}
            phoneNumber={phoneNumber}
            editNameStatus={editNameStatus}
            editLanguageStatus={editLanguageStatus}
            editSecondaryEmailStatus={editSecondaryEmailStatus}
            pictureStatus={pictureStatus}
            setPictureStatus={setPictureStatus}
            showPictureStatus={showPictureStatus}
          />
        )}
      </LoadingIcon>
    </MarginWrapper>
  )
}

export default MyAccount
